// src/pages/PrivacyPage.js
// Revisione 29/09/2026: testo allineato ai trattamenti effettivamente presenti nel codice
// (hosting Firebase, Google Analytics 4 con Consent Mode, moduli via client email, video
// YouTube caricati al clic, memoria locale del browser). Rimossi elementi non verificati
// (periodi di conservazione di terzi, garanzie contrattuali dichiarate, obblighi AML,
// comunicazioni a OCF, consenso marketing). Titolare del trattamento indicato per nome su
// richiesta esplicita del titolare (OWNER_NAME in config/compliance, usato solo qui e in Trasparenza).
import React, { useContext } from "react";
import "./PrivacyPage.css";
import Seo from "../components/Seo";
import { LangContext } from "../lang/LangProvider";
import { OWNER_NAME } from "../config/compliance";

const EMAIL = "alphaframeanalytics.it@gmail.com";
const SITE = "alphaweb-93f02.web.app";

const PrivacyPage = () => {
  const { lang } = useContext(LangContext);

  const seoTitle = lang === "en" ? "Privacy Policy" : "Privacy Policy";
  const seoDesc =
    lang === "en"
      ? "Privacy policy of the AlphaFrame Analytics website: what data is processed, why, by which providers, and your rights."
      : "Informativa privacy del sito AlphaFrame Analytics: quali dati sono trattati, per quali finalità, tramite quali fornitori e quali sono i tuoi diritti.";

  const content = {
    it: {
      title: "Privacy Policy",
      lastUpdated: "Ultimo aggiornamento: settembre 2026",
      sections: [
        {
          title: "1. Titolare del trattamento",
          content: `Titolare del trattamento dei dati personali raccolti tramite il sito ${SITE} è ${OWNER_NAME}, titolare del sito.
Contatto per qualsiasi richiesta relativa ai dati personali: ${EMAIL}`,
        },
        {
          title: "2. Navigazione e hosting",
          content: `Il sito è ospitato su Firebase Hosting (Google). Per mostrare le pagine, il fornitore riceve i dati tecnici di ogni richiesta, tra cui indirizzo IP, tipo di browser e dispositivo, pagina richiesta, data e ora.
Finalità: erogazione del sito e sicurezza. Base giuridica: legittimo interesse al funzionamento del sito (art. 6.1.f GDPR).`,
        },
        {
          title: "3. Statistiche di utilizzo (Google Analytics 4)",
          content: `Il sito usa Google Analytics 4 (G-XR3R9BPH8S) con Google Consent Mode.
- Il tag di Google viene caricato a ogni visita. Finché non accetti i cookie statistici, i cookie di Analytics non vengono impostati; il tag può comunque inviare a Google segnali senza cookie sulle pagine visitate e sugli eventi d'uso.
- Se accetti i cookie statistici dal banner, Analytics usa cookie per misurare le visite.
- Gli eventi registrati riguardano l'uso del sito: pagine viste, apertura e lettura degli articoli, uso degli strumenti, clic sui pulsanti, lingua. Alcuni eventi includono valori sintetici ricavati dagli input: fascia di spesa annua e anni stimati nel calcolatore FIRE, numero di componenti e orizzonte nel simulatore ETF, testo delle ricerche nella Knowledge Base, motivo selezionato nel modulo di contatto. Gli importi inseriti negli strumenti non vengono inviati.
Finalità: statistiche sull'uso dei contenuti e degli strumenti.`,
        },
        {
          title: "4. Richieste di contatto e di aggiornamenti",
          content: `I moduli "Invia un messaggio" e "Iscriviti agli aggiornamenti" non inviano dati al sito: aprono il tuo programma di posta con un messaggio precompilato indirizzato a ${EMAIL} (casella Gmail, Google). Il messaggio viene spedito solo se lo invii tu.
Dati: nome, indirizzo email, motivo e testo del messaggio, oltre ai dati che il tuo programma di posta aggiunge normalmente.
- Richieste di contatto: i dati servono solo a rispondere alla richiesta (art. 6.1.b GDPR) e sono conservati per il tempo necessario a gestirla. Una richiesta di contatto non comporta l'iscrizione agli aggiornamenti né l'invio di comunicazioni promozionali.
- Richieste di aggiornamenti: al momento non vengono inviate email periodiche. Se in futuro verranno inviati aggiornamenti, riguarderanno solo i nuovi contenuti del sito e saranno spediti solo a chi li ha richiesti, sulla base della richiesta stessa (art. 6.1.a GDPR). Puoi revocarla in qualsiasi momento scrivendo a ${EMAIL}.`,
        },
        {
          title: "5. Video YouTube",
          content: `I video sono incorporati tramite il dominio youtube-nocookie.com e vengono caricati solo quando fai clic sul riquadro del video. Da quel momento YouTube (Google) riceve i dati tecnici della connessione, tra cui l'indirizzo IP, e può memorizzare informazioni sul dispositivo secondo le proprie regole.`,
        },
        {
          title: "6. Memoria locale del browser",
          content: `Il sito salva nel tuo browser (localStorage) due informazioni tecniche, che non vengono inviate al sito:
- la scelta espressa nel banner dei cookie, con data e ora;
- la lingua selezionata.
Puoi cancellarle dalle impostazioni del browser: al successivo accesso il banner dei cookie verrà riproposto, così potrai modificare o revocare la scelta sui cookie statistici.`,
        },
        {
          title: "7. Strumenti di simulazione",
          content: `I calcoli dei simulatori e l'esercizio Risk Lab avvengono nel tuo browser: i valori inseriti e i risultati non vengono trasmessi né memorizzati dal sito (restano salvi gli eventi statistici descritti al punto 3).`,
        },
        {
          title: "8. Fornitori e trasferimenti",
          content: `I fornitori coinvolti sono servizi di Google (Firebase Hosting, Google Analytics, Gmail, YouTube). Questi fornitori possono trattare i dati anche fuori dall'Unione europea, in particolare negli Stati Uniti, secondo i rispettivi termini di servizio e di protezione dei dati.`,
        },
        {
          title: "9. Diritti dell'interessato",
          content: `Ai sensi del GDPR puoi chiedere l'accesso ai dati (art. 15), la rettifica (art. 16), la cancellazione (art. 17), la limitazione (art. 18), la portabilità (art. 20) e opporti al trattamento (art. 21). Puoi revocare in qualsiasi momento il consenso prestato, senza pregiudicare la liceità del trattamento precedente.
Per esercitare i diritti: ${EMAIL}`,
        },
        {
          title: "10. Reclami",
          content: `Se ritieni che il trattamento dei tuoi dati violi il GDPR, puoi presentare reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).`,
        },
        {
          title: "11. Modifiche",
          content: `Questa informativa può essere aggiornata. Le modifiche sono pubblicate su questa pagina con la data di ultimo aggiornamento.`,
        },
      ],
    },
    en: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: September 2026",
      sections: [
        {
          title: "1. Data controller",
          content: `The controller of personal data collected through the website ${SITE} is ${OWNER_NAME}, the website owner.
Contact for any request about personal data: ${EMAIL}`,
        },
        {
          title: "2. Browsing and hosting",
          content: `The website is hosted on Firebase Hosting (Google). To serve the pages, the provider receives the technical data of each request, including IP address, browser and device type, requested page, date and time.
Purpose: delivering the website and security. Legal basis: legitimate interest in operating the website (art. 6.1.f GDPR).`,
        },
        {
          title: "3. Usage statistics (Google Analytics 4)",
          content: `The website uses Google Analytics 4 (G-XR3R9BPH8S) with Google Consent Mode.
- The Google tag is loaded on every visit. Until you accept statistics cookies, Analytics cookies are not set; the tag may still send Google cookieless signals about the pages visited and usage events.
- If you accept statistics cookies in the banner, Analytics uses cookies to measure visits.
- The events recorded concern website usage: page views, opening and reading articles, tool usage, button clicks, language. Some events include summary values derived from inputs: annual spending bracket and estimated years in the FIRE calculator, number of components and horizon in the ETF simulator, Knowledge Base search text, reason selected in the contact form. Amounts entered in the tools are not sent.
Purpose: statistics on the use of content and tools.`,
        },
        {
          title: "4. Contact and update requests",
          content: `The "Send us a message" and "Subscribe to updates" forms do not send data to the website: they open your email program with a pre-filled message addressed to ${EMAIL} (a Gmail mailbox, Google). The message is sent only if you send it.
Data: name, email address, reason and text of the message, plus the data your email program normally adds.
- Contact requests: the data is used only to reply to the request (art. 6.1.b GDPR) and is kept for as long as needed to handle it. A contact request does not subscribe you to updates or to promotional communications.
- Update requests: no periodic emails are sent at the moment. If updates are sent in the future, they will concern only new website content and will be sent only to those who asked for them, based on that request (art. 6.1.a GDPR). You can withdraw it at any time by writing to ${EMAIL}.`,
        },
        {
          title: "5. YouTube videos",
          content: `Videos are embedded via the youtube-nocookie.com domain and are loaded only when you click the video frame. From then on YouTube (Google) receives the technical data of the connection, including the IP address, and may store information on the device according to its own rules.`,
        },
        {
          title: "6. Browser local storage",
          content: `The website stores two technical items in your browser (localStorage), which are not sent to the website:
- the choice made in the cookie banner, with date and time;
- the selected language.
You can delete them from your browser settings: on your next visit the cookie banner will be shown again, so you can change or withdraw your choice on statistics cookies.`,
        },
        {
          title: "7. Simulation tools",
          content: `Simulator calculations and the Risk Lab exercise run in your browser: the values entered and the results are neither transmitted nor stored by the website (except for the statistics events described in section 3).`,
        },
        {
          title: "8. Providers and transfers",
          content: `The providers involved are Google services (Firebase Hosting, Google Analytics, Gmail, YouTube). These providers may process data outside the European Union as well, in particular in the United States, under their respective terms of service and data protection terms.`,
        },
        {
          title: "9. Data subject rights",
          content: `Under the GDPR you may request access to your data (art. 15), rectification (art. 16), erasure (art. 17), restriction (art. 18), portability (art. 20) and object to processing (art. 21). You may withdraw any consent given at any time, without affecting the lawfulness of prior processing.
To exercise your rights: ${EMAIL}`,
        },
        {
          title: "10. Complaints",
          content: `If you believe the processing of your data breaches the GDPR, you may lodge a complaint with the Italian Data Protection Authority (Garante per la protezione dei dati personali, www.garanteprivacy.it).`,
        },
        {
          title: "11. Changes",
          content: `This policy may be updated. Changes are published on this page with the date of last update.`,
        },
      ],
    },
  };

  const pageContent = lang === "en" ? content.en : content.it;

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={`/${lang}/privacy`}
      />

      <div className="privacy-page-bg">
        <div className="privacy-container">
          <header className="privacy-header">
            <h1>{pageContent.title}</h1>
            <p className="privacy-last-updated">{pageContent.lastUpdated}</p>
          </header>

          <div className="privacy-content">
            {pageContent.sections.map((section, idx) => (
              <section key={idx} className="privacy-section">
                <h2>{section.title}</h2>
                <p style={{ whiteSpace: "pre-wrap" }}>{section.content}</p>
              </section>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPage;
